import {
  Box,
  Button,
  Checkbox,
  Collapsible,
  Field,
  Grid,
  HStack,
  Input,
  NativeSelect,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { SegmentedControl } from '@ui';
import React from 'react';
import { LuChevronDown, LuRotateCcw } from 'react-icons/lu';
import { TrackingType } from '@/types';
import { fromDisplay, toDisplay } from '../utils';

interface SettingsPanelProps {
  formats: TrackingType.FormatsResponse;
  labelSpec: TrackingType.LabelSpec;
  profileSpec: TrackingType.ProfileSpec;
  unit: 'mm' | 'in';
  onUnit: (unit: 'mm' | 'in') => void;
  onLabel: (spec: TrackingType.LabelSpec) => void;
  onProfile: (spec: TrackingType.ProfileSpec) => void;
  fieldErrors: Record<string, string>;
  mode: 'labels' | 'reports';
  paper: 'letter' | 'a4';
  onPaper: (paper: 'letter' | 'a4') => void;
  onCalibration?: () => void;
  calibrating?: boolean;
}

const NumberField: React.FC<{
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
  unit?: string;
  error?: string;
  allowEmpty?: boolean;
  min?: number;
  helper?: string;
}> = ({
  label,
  value,
  onChange,
  step = 0.1,
  unit,
  error,
  allowEmpty,
  min,
  helper,
}) => {
  const [text, setText] = React.useState(
    value === null || value === undefined ? '' : String(value),
  );
  React.useEffect(() => {
    const parsed = Number(text);
    if (value === null && text === '') return;
    if (!Number.isFinite(parsed) || parsed !== value)
      setText(value === null || value === undefined ? '' : String(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Field.Root invalid={Boolean(error)}>
      <Field.Label fontSize="xs">
        {label}
        {unit ? ` (${unit})` : ''}
      </Field.Label>
      <Input
        size="sm"
        inputMode="decimal"
        type="number"
        step={step}
        min={min}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (e.target.value === '' && allowEmpty) {
            onChange(null);
            return;
          }
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
      {error ? (
        <Field.ErrorText>{error}</Field.ErrorText>
      ) : helper ? (
        <Field.HelperText>{helper}</Field.HelperText>
      ) : null}
    </Field.Root>
  );
};

const Select: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  error?: string;
}> = ({ label, value, onChange, children, error }) => (
  <Field.Root invalid={Boolean(error)}>
    <Field.Label fontSize="xs">{label}</Field.Label>
    <NativeSelect.Root size="sm">
      <NativeSelect.Field
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
    {error && <Field.ErrorText>{error}</Field.ErrorText>}
  </Field.Root>
);

const Details: React.FC<{
  title: string;
  summary?: string;
  forceOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, summary, forceOpen, children }) => {
  const [open, setOpen] = React.useState(Boolean(forceOpen));
  React.useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Collapsible.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          w="100%"
          justifyContent="space-between"
          px={2}
          fontWeight="semibold"
        >
          <HStack gap={2}>
            <LuChevronDown
              style={{ transform: open ? 'rotate(180deg)' : undefined }}
            />
            {title}
          </HStack>
          {summary && !open ? (
            <Text fontSize="xs" color="fg.muted" fontWeight="normal" truncate>
              {summary}
            </Text>
          ) : null}
        </Button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Stack gap={2} pt={2} pb={1}>
          {children}
        </Stack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  formats,
  labelSpec,
  profileSpec,
  unit,
  onUnit,
  onLabel,
  onProfile,
  fieldErrors,
  mode,
  paper,
  onPaper,
  onCalibration,
  calibrating,
}) => {
  const [advancedSearch, setAdvancedSearch] = React.useState('');
  const qualified = formats.formats.filter((f) => f.status === 'qualified');
  const codes = labelSpec.codes;
  const simple =
    codes.mode === 'both'
      ? 'both'
      : codes.primary === 'qr'
        ? 'qr'
        : codes.primary === 'code128'
          ? 'code128'
          : 'other';
  const setCodes = (patch: Partial<TrackingType.LabelSpec['codes']>) =>
    onLabel({ ...labelSpec, codes: { ...codes, ...patch } });
  const setLabelDims = (patch: Partial<TrackingType.LabelSpec['label']>) =>
    onLabel({ ...labelSpec, label: { ...labelSpec.label, ...patch } });
  const presets = [...formats.presets.roll, ...formats.presets.sheet];
  const currentPreset = presets.find(
    (p) =>
      Math.abs(p.width_mm - labelSpec.label.width_mm) < 0.01 &&
      Math.abs(p.height_mm - labelSpec.label.height_mm) < 0.01 &&
      (p.sheet
        ? profileSpec.media === 'sheet' &&
          profileSpec.sheet?.columns === p.sheet.columns
        : profileSpec.media === 'roll'),
  );
  const fieldLabels = Object.fromEntries(
    formats.fields.map((f) => [f.key, f.label]),
  );
  const primaryFormat = qualified.find((f) => f.id === codes.primary);
  const advancedList = formats.formats
    .filter((f) => f.id !== 'qr' && f.id !== 'code128')
    .filter(
      (f) =>
        !advancedSearch ||
        f.label.toLowerCase().includes(advancedSearch.toLowerCase()),
    );

  const applyPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    onUnit(preset.unit);
    onLabel({
      ...labelSpec,
      label: {
        ...labelSpec.label,
        width_mm: preset.width_mm,
        height_mm: preset.height_mm,
        shape:
          labelSpec.label.shape === 'square' ||
          labelSpec.label.shape === 'round'
            ? 'rectangle'
            : labelSpec.label.shape,
      },
      orientation: preset.sheet ? 'normal' : labelSpec.orientation,
    });
    onProfile(
      preset.sheet
        ? {
            ...profileSpec,
            media: 'sheet',
            sheet: { ...preset.sheet, start_cell: 1, skip_cells: [] },
          }
        : { ...profileSpec, media: 'roll', sheet: null },
    );
  };

  if (mode === 'reports') {
    return (
      <Stack gap={3}>
        <Select
          label="Paper"
          value={paper}
          onChange={(v) => onPaper(v as 'letter' | 'a4')}
        >
          <option value="letter">Letter (8.5 × 11 in)</option>
          <option value="a4">A4 (210 × 297 mm)</option>
        </Select>
        <Text textStyle="body.small" color="fg.muted">
          Each report uses the latest authorized information when you prepare
          it. A printed report is a dated snapshot; its QR code still opens the
          live report.
        </Text>
      </Stack>
    );
  }

  const sheet = profileSpec.sheet;
  const setSheet = (patch: Partial<TrackingType.SheetSpec>) =>
    onProfile({
      ...profileSpec,
      sheet: { ...(sheet as TrackingType.SheetSpec), ...patch },
    });
  const u = unit;

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Text textStyle="fieldLabel.bold" id="format-label">
          1. What code to print
        </Text>
        <SegmentedControl
          size="sm"
          value={simple === 'other' ? '' : simple}
          aria-labelledby="format-label"
          items={[
            { value: 'qr', label: 'QR' },
            { value: 'code128', label: 'Barcode' },
            { value: 'both', label: 'QR + Barcode' },
          ]}
          onValueChange={(e: { value: string | null }) => {
            if (e.value === 'qr')
              setCodes({
                mode: 'single',
                primary: 'qr',
                primary_payload: 'URL',
              });
            else if (e.value === 'code128')
              setCodes({
                mode: 'single',
                primary: 'code128',
                primary_payload: 'CANONICAL',
              });
            else if (e.value === 'both')
              setCodes({
                mode: 'both',
                primary: 'qr',
                primary_payload: 'URL',
                secondary: 'code128',
                secondary_payload: 'CANONICAL',
              });
          }}
        />
        <Text textStyle="body.small" color="fg.muted">
          {codes.mode === 'both'
            ? 'QR opens the live report on a phone. The barcode works with a handheld scanner. Both identify the same item.'
            : simple === 'qr'
              ? 'QR opens the live report on a phone. No extra setup is required.'
              : simple === 'code128'
                ? 'A linear barcode for a handheld scanner. The same code identifies the item.'
                : primaryFormat?.description}
        </Text>
        <Details
          title="Other code types"
          summary={simple === 'other' ? primaryFormat?.label : 'QR, barcode, or both'}
          forceOpen={simple === 'other'}
        >
          <Field.Root>
            <Field.Label fontSize="xs">Search other formats</Field.Label>
            <Input
              size="sm"
              placeholder="Search formats"
              value={advancedSearch}
              onChange={(e) => setAdvancedSearch(e.target.value)}
              aria-label="Search code formats"
            />
            <NativeSelect.Root size="sm" mt={2}>
              <NativeSelect.Field
                aria-label="More formats"
                value={simple === 'other' ? codes.primary : ''}
                onChange={(e) => {
                  const f = formats.formats.find((x) => x.id === e.target.value);
                  if (!f || f.status !== 'qualified') return;
                  setCodes({
                    mode: 'single',
                    primary: f.id,
                    primary_payload: f.default_payload,
                  });
                }}
              >
                <option value="">
                  {simple === 'other'
                    ? primaryFormat?.label
                    : 'Choose another format'}
                </option>
                {advancedList.map((f) => (
                  <option
                    key={f.id}
                    value={f.id}
                    disabled={f.status !== 'qualified'}
                    title={
                      f.status === 'qualified'
                        ? `${f.description} ${f.scanner_note}`
                        : f.unavailable_reason
                    }
                  >
                    {f.label}
                    {f.status !== 'qualified' ? ' — not available' : ''}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            {simple === 'other' && primaryFormat?.scanner_note && (
              <Field.HelperText>{primaryFormat.scanner_note}</Field.HelperText>
            )}
          </Field.Root>
        </Details>
      </Stack>

      <Stack gap={2}>
        <HStack justify="space-between">
          <Text textStyle="fieldLabel.bold">2. Label size</Text>
          <SegmentedControl
            size="xs"
            value={unit}
            aria-label="Units"
            items={[
              { value: 'mm', label: 'mm' },
              { value: 'in', label: 'in' },
            ]}
            onValueChange={(e: { value: string | null }) =>
              onUnit(e.value as 'mm' | 'in')
            }
          />
        </HStack>
        <Select
          label="Stock preset"
          value={currentPreset?.id ?? 'custom'}
          onChange={applyPreset}
        >
          <option value="custom">Custom size</option>
          <optgroup label="Thermal roll">
            {formats.presets.roll.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Office label sheets">
            {formats.presets.sheet.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </optgroup>
        </Select>
        <Select
          label="Stock"
          value={profileSpec.media}
          onChange={(v) =>
            onProfile(
              v === 'sheet'
                ? {
                    ...profileSpec,
                    media: 'sheet',
                    sheet: profileSpec.sheet ?? {
                      page: 'letter',
                      page_width_mm: 215.9,
                      page_height_mm: 279.4,
                      columns: 1,
                      rows: 1,
                      left_mm: 10,
                      top_mm: 10,
                      pitch_x_mm: labelSpec.label.width_mm,
                      pitch_y_mm: labelSpec.label.height_mm,
                      start_cell: 1,
                      skip_cells: [],
                    },
                  }
                : { ...profileSpec, media: 'roll', sheet: null },
            )
          }
        >
          <option value="roll">Roll — one label per page</option>
          <option value="sheet">Sheet — several labels per page</option>
        </Select>
        <Grid templateColumns="1fr 1fr" gap={2}>
          <NumberField
            label="Width"
            unit={u}
            step={u === 'mm' ? 0.5 : 0.05}
            value={toDisplay(labelSpec.label.width_mm, u)}
            onChange={(v) =>
              v !== null && setLabelDims({ width_mm: fromDisplay(v, u) })
            }
            error={fieldErrors['label.width_mm']}
          />
          <NumberField
            label="Height"
            unit={u}
            step={u === 'mm' ? 0.5 : 0.05}
            value={toDisplay(labelSpec.label.height_mm, u)}
            onChange={(v) =>
              v !== null && setLabelDims({ height_mm: fromDisplay(v, u) })
            }
            error={fieldErrors['label.height_mm']}
          />
        </Grid>
        <Details
          title="Stock details"
          summary={`${labelSpec.label.shape}, ${labelSpec.orientation === 'rotated' ? 'rotated' : 'as designed'}`}
          forceOpen={Boolean(
            fieldErrors['label.shape'] ||
            fieldErrors['label.margin_mm'] ||
            fieldErrors.orientation,
          )}
        >
          <Grid templateColumns="1fr 1fr" gap={2}>
            <Select
              label="Shape"
              value={labelSpec.label.shape}
              onChange={(v) =>
                setLabelDims({
                  shape: v as TrackingType.LabelSpec['label']['shape'],
                })
              }
              error={fieldErrors['label.shape']}
            >
              <option value="rectangle">Rectangle</option>
              <option value="square">Square</option>
              <option value="round">Round</option>
              <option value="oval">Oval</option>
            </Select>
            <Select
              label="Orientation"
              value={labelSpec.orientation}
              onChange={(v) =>
                onLabel({
                  ...labelSpec,
                  orientation: v as 'normal' | 'rotated',
                })
              }
              error={fieldErrors.orientation}
            >
              <option value="normal">As designed</option>
              <option value="rotated">Rotated 90° (roll feed)</option>
            </Select>
            <NumberField
              label="Margin / safe area"
              unit={u}
              step={u === 'mm' ? 0.5 : 0.02}
              value={toDisplay(labelSpec.label.margin_mm, u)}
              onChange={(v) =>
                v !== null && setLabelDims({ margin_mm: fromDisplay(v, u) })
              }
              error={fieldErrors['label.margin_mm']}
            />
            {(labelSpec.label.shape === 'rectangle' ||
              labelSpec.label.shape === 'square') && (
              <NumberField
                label="Corner radius"
                unit={u}
                step={u === 'mm' ? 0.5 : 0.02}
                value={toDisplay(labelSpec.label.corner_radius_mm, u)}
                onChange={(v) =>
                  v !== null &&
                  setLabelDims({ corner_radius_mm: fromDisplay(v, u) })
                }
              />
            )}
          </Grid>
          <Text textStyle="body.small" color="fg.muted">
            Shapes keep content inside a safe area. The printer does not cut
            the shape.
          </Text>
        </Details>
      </Stack>

      <Stack gap={2}>
        <Text textStyle="fieldLabel.bold">3. Text on the label</Text>
        <Select
          label="Arrangement"
          value={labelSpec.arrangement}
          onChange={(v) =>
            onLabel({
              ...labelSpec,
              arrangement: v as TrackingType.LabelSpec['arrangement'],
            })
          }
        >
          <option value="side_by_side">Code beside text</option>
          <option value="stacked">Code above text</option>
          <option value="code_only">Code only</option>
        </Select>
        <Switch.Root
          checked={labelSpec.human_readable}
          onCheckedChange={(e) =>
            onLabel({ ...labelSpec, human_readable: e.checked })
          }
          size="sm"
        >
          <Switch.HiddenInput />
          <Switch.Control />
          <Switch.Label>
            Print the code as readable text under barcodes
          </Switch.Label>
        </Switch.Root>
        <Text fontSize="xs" color="fg.muted">
          Turn on the words that should appear next to the code. Scanning the
          code still opens the latest information.
        </Text>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={1}>
          {labelSpec.fields.map((f, i) => (
            <Checkbox.Root
              key={f.key}
              size="sm"
              checked={f.visible}
              onCheckedChange={(e) =>
                onLabel({
                  ...labelSpec,
                  fields: labelSpec.fields.map((x, j) =>
                    j === i ? { ...x, visible: Boolean(e.checked) } : x,
                  ),
                })
              }
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>{fieldLabels[f.key] ?? f.key}</Checkbox.Label>
            </Checkbox.Root>
          ))}
        </Grid>
        <Details
          title="Layout details"
          summary={`${labelSpec.title_pt} pt title`}
          forceOpen={Boolean(fieldErrors.title_pt || fieldErrors.font_pt)}
        >
          <Grid templateColumns="1fr 1fr" gap={2}>
            <Select
              label="Code position"
              value={labelSpec.code_position}
              onChange={(v) =>
                onLabel({ ...labelSpec, code_position: v as 'start' | 'end' })
              }
            >
              <option value="start">Left / top</option>
              <option value="end">Right / bottom</option>
            </Select>
            <Box />
            <NumberField
              label="Title size"
              unit="pt"
              step={0.5}
              value={labelSpec.title_pt}
              onChange={(v) =>
                v !== null && onLabel({ ...labelSpec, title_pt: v })
              }
              error={fieldErrors.title_pt}
            />
            <NumberField
              label="Text size"
              unit="pt"
              step={0.5}
              value={labelSpec.font_pt}
              onChange={(v) =>
                v !== null && onLabel({ ...labelSpec, font_pt: v })
              }
              error={fieldErrors.font_pt}
            />
          </Grid>
        </Details>
      </Stack>

      <Details
        title="Print quality details"
        forceOpen={Boolean(
          fieldErrors['codes.module_mm'] ||
          fieldErrors['codes.bar_height_mm'] ||
          fieldErrors['codes.primary_payload'],
        )}
      >
        <Grid templateColumns="1fr 1fr" gap={2}>
          {primaryFormat &&
            primaryFormat.payload_modes.length > 1 &&
            codes.mode === 'single' &&
            !(codes.primary === 'qr' && codes.primary_payload === 'URL') && (
              <Select
                label="Code content"
                value={codes.primary_payload}
                onChange={(v) =>
                  setCodes({
                    primary_payload: v as TrackingType.PayloadMode,
                  })
                }
                error={fieldErrors['codes.primary_payload']}
              >
                {primaryFormat.payload_modes.map((m) => (
                  <option key={m} value={m}>
                    {formats.payload_modes[m]}
                  </option>
                ))}
              </Select>
            )}
            {(codes.primary === 'qr' || codes.mode === 'both') && (
              <Select
                label="QR error correction"
                value={codes.qr_ecc}
                onChange={(v) =>
                  setCodes({ qr_ecc: v as 'L' | 'M' | 'Q' | 'H' })
                }
              >
                <option value="M">M — recommended (15%)</option>
                <option value="Q">Q — 25% (larger code)</option>
                <option value="H">H — 30% (largest code)</option>
                <option value="L">L — 7% (smallest code)</option>
              </Select>
            )}
            {codes.mode === 'both' && (
              <Select
                label="Barcode format"
                value={codes.secondary}
                onChange={(v) => {
                  const f = qualified.find((x) => x.id === v);
                  setCodes({
                    secondary: v,
                    secondary_payload: f?.default_payload ?? 'CANONICAL',
                  });
                }}
              >
                {qualified
                  .filter((f) => f.family === 'linear')
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
              </Select>
            )}
            <NumberField
              label="Module / bar width"
              unit="mm"
              allowEmpty
              step={0.01}
              value={codes.module_mm}
              onChange={(v) => setCodes({ module_mm: v })}
              helper="Empty = largest that fits"
              error={fieldErrors['codes.module_mm']}
            />
            <NumberField
              label="Bar height"
              unit="mm"
              allowEmpty
              step={0.5}
              value={codes.bar_height_mm}
              onChange={(v) => setCodes({ bar_height_mm: v })}
              helper="Empty = automatic"
              error={fieldErrors['codes.bar_height_mm']}
            />
          </Grid>
      </Details>

      <Details
        title="Printer settings"
        summary={
          profileSpec.dpi ? `${profileSpec.dpi} dpi` : 'Resolution not set'
        }
        forceOpen={Boolean(
          fieldErrors.dpi || fieldErrors.offset_x_mm || fieldErrors.offset_y_mm,
        )}
      >
        <Grid templateColumns="1fr 1fr" gap={2}>
          <Select
            label="Printer resolution"
            value={String(profileSpec.dpi ?? 'vector')}
            onChange={(v) =>
              onProfile({
                ...profileSpec,
                dpi: v === 'vector' ? null : (Number(v) as 203 | 300 | 600),
              })
            }
            error={fieldErrors.dpi}
          >
            <option value="203">203 dpi (common thermal)</option>
            <option value="300">300 dpi</option>
            <option value="600">600 dpi (laser)</option>
            <option value="vector">Unknown (vector)</option>
          </Select>
          <NumberField
            label="Calibration X"
            unit="mm"
            step={0.1}
            value={profileSpec.offset_x_mm}
            onChange={(v) =>
              v !== null && onProfile({ ...profileSpec, offset_x_mm: v })
            }
            error={fieldErrors.offset_x_mm}
          />
          <NumberField
            label="Calibration Y"
            unit="mm"
            step={0.1}
            value={profileSpec.offset_y_mm}
            onChange={(v) =>
              v !== null && onProfile({ ...profileSpec, offset_y_mm: v })
            }
            error={fieldErrors.offset_y_mm}
          />
        </Grid>
        <Box>
          <Button
            size="xs"
            variant="ghost"
            onClick={() =>
              onProfile({ ...profileSpec, offset_x_mm: 0, offset_y_mm: 0 })
            }
          >
            <LuRotateCcw /> Reset calibration
          </Button>
          {onCalibration && (
            <Button
              size="xs"
              variant="outline"
              onClick={onCalibration}
              loading={calibrating}
            >
              Print calibration sheet
            </Button>
          )}
        </Box>
      </Details>
      {profileSpec.media === 'sheet' && sheet && (
        <Stack gap={2}>
          <Text textStyle="fieldLabel.bold">Sheet placement</Text>
          <Grid templateColumns="1fr 1fr" gap={2}>
            <Select
              label="Paper"
              value={sheet.page}
              onChange={(v) => {
                const page = formats.presets.pages[v];
                setSheet({
                  page: v as TrackingType.SheetSpec['page'],
                  ...(page
                    ? {
                        page_width_mm: page.width_mm,
                        page_height_mm: page.height_mm,
                      }
                    : {}),
                });
              }}
            >
              <option value="letter">Letter</option>
              <option value="a4">A4</option>
              <option value="custom">Custom</option>
            </Select>
            <NumberField
              label="Start at cell"
              step={1}
              min={1}
              value={sheet.start_cell ?? 1}
              onChange={(v) =>
                v !== null &&
                setSheet({ start_cell: Math.max(1, Math.round(v)) })
              }
              error={fieldErrors['sheet.start_cell']}
              helper="Skip used labels on a partial sheet"
            />
          </Grid>
          <Details
            title="Custom sheet grid"
            summary={`${sheet.columns} × ${sheet.rows}`}
            forceOpen={Object.keys(fieldErrors).some((k) =>
              k.startsWith('sheet'),
            )}
          >
            <Grid templateColumns="1fr 1fr" gap={2}>
            {sheet.page === 'custom' && (
              <>
                <NumberField
                  label="Page width"
                  unit={u}
                  value={toDisplay(sheet.page_width_mm, u)}
                  onChange={(v) =>
                    v !== null && setSheet({ page_width_mm: fromDisplay(v, u) })
                  }
                />
                <NumberField
                  label="Page height"
                  unit={u}
                  value={toDisplay(sheet.page_height_mm, u)}
                  onChange={(v) =>
                    v !== null &&
                    setSheet({ page_height_mm: fromDisplay(v, u) })
                  }
                />
              </>
            )}
            <NumberField
              label="Columns"
              step={1}
              value={sheet.columns}
              onChange={(v) =>
                v !== null && setSheet({ columns: Math.round(v) })
              }
              error={fieldErrors['sheet.columns']}
            />
            <NumberField
              label="Rows"
              step={1}
              value={sheet.rows}
              onChange={(v) => v !== null && setSheet({ rows: Math.round(v) })}
              error={fieldErrors['sheet.rows']}
            />
            <NumberField
              label="Left margin"
              unit={u}
              value={toDisplay(sheet.left_mm, u)}
              onChange={(v) =>
                v !== null && setSheet({ left_mm: fromDisplay(v, u) })
              }
            />
            <NumberField
              label="Top margin"
              unit={u}
              value={toDisplay(sheet.top_mm, u)}
              onChange={(v) =>
                v !== null && setSheet({ top_mm: fromDisplay(v, u) })
              }
            />
            <NumberField
              label="Horizontal pitch"
              unit={u}
              value={toDisplay(sheet.pitch_x_mm, u)}
              onChange={(v) =>
                v !== null && setSheet({ pitch_x_mm: fromDisplay(v, u) })
              }
              helper="Label width + gap"
            />
            <NumberField
              label="Vertical pitch"
              unit={u}
              value={toDisplay(sheet.pitch_y_mm, u)}
              onChange={(v) =>
                v !== null && setSheet({ pitch_y_mm: fromDisplay(v, u) })
              }
              helper="Label height + gap"
            />
            <Field.Root invalid={Boolean(fieldErrors['sheet.skip_cells'])}>
              <Field.Label fontSize="xs">Skip cells</Field.Label>
              <Input
                size="sm"
                placeholder="e.g. 4, 9"
                defaultValue={(sheet.skip_cells ?? []).join(', ')}
                onBlur={(e) =>
                  setSheet({
                    skip_cells: e.target.value
                      .split(',')
                      .map((x) => Number(x.trim()))
                      .filter((n) => Number.isInteger(n) && n > 0),
                  })
                }
              />
              <Field.HelperText>
                Numbered left to right, top to bottom.
              </Field.HelperText>
              {fieldErrors['sheet.skip_cells'] && (
                <Field.ErrorText>
                  {fieldErrors['sheet.skip_cells']}
                </Field.ErrorText>
              )}
            </Field.Root>
            </Grid>
          </Details>
        </Stack>
      )}
    </Stack>
  );
};
